import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  LogOut, Radar, ChevronDown, Building, Mail,
  GripVertical, Eye, EyeOff, Save, XCircle, Sliders,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { NAV_CATALOG, composeSidebar } from "@/lib/navCatalog";

/* ----------------------- Tenant switcher (super-admin) ----------------------- */

function TenantSwitcher() {
  const { user, isSuperAdmin, setActiveTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const activeTenantId = typeof window !== "undefined"
    ? (localStorage.getItem("forensiq_active_tenant") || user?.tenant_id)
    : user?.tenant_id;

  useEffect(() => {
    if (!isSuperAdmin() || !open) return;
    setLoading(true);
    api.get("/platform/tenants")
      .then((r) => setTenants(r.data.tenants || []))
      .catch(() => setTenants([]))
      .finally(() => setLoading(false));
  }, [open, isSuperAdmin]);

  if (!isSuperAdmin()) return null;

  const activeTenantName =
    tenants.find((t) => t.id === activeTenantId)?.name
    || (activeTenantId === user?.tenant_id ? (user?.tenant?.name || "Platform") : activeTenantId || "Platform");

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="tenant-switcher-btn"
        className="w-full flex items-center gap-2 px-3 py-2 mb-2 border border-[#B57BFF]/50 hover:border-[#B57BFF] rounded-sm text-xs bg-[#0a0a0a] transition-colors">
        <Building className="w-3.5 h-3.5 text-[#B57BFF]" strokeWidth={1.75} />
        <div className="flex-1 text-left min-w-0">
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#B57BFF]">Tenant</div>
          <div className="truncate text-white text-xs">{activeTenantName}</div>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-[#8E9196] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 bg-[#0a0a0a] border border-white/10 rounded-sm shadow-xl max-h-64 overflow-y-auto"
             data-testid="tenant-switcher-list">
          {loading && <div className="p-3 text-[10px] font-mono text-[#52525B]">Loading…</div>}
          {!loading && tenants.map((t) => (
            <button key={t.id} data-testid={`tenant-switch-${t.id}`}
              onClick={() => { setActiveTenant(t.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-white/[0.04] transition-colors border-b border-white/[0.03] last:border-0 ${
                t.id === activeTenantId ? "bg-white/[0.03] text-[#00F0FF]" : "text-white"
              }`}>
              <div className="flex items-center gap-2">
                <span className="truncate flex-1">{t.name}</span>
                <span className="text-[9px] font-mono uppercase tracking-widest text-[#52525B]">{t.plan}</span>
              </div>
              <div className="text-[9px] font-mono text-[#52525B] mt-0.5">
                {t.user_count} users · {t.investigation_count} cases · {t.kiosk_count} walls
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------- Sidebar customization state ----------------------- */

function useSidebarPrefs(user) {
  const [prefs, setPrefs] = useState({ layout: [], source: "default" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get("/sidebar/prefs")
      .then((r) => setPrefs({ layout: r.data.layout || [], source: r.data.source || "default" }))
      .catch(() => setPrefs({ layout: [], source: "default" }))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const save = async (layout) => {
    const payload = { layout: layout.map(({ key, hidden }) => ({ key, hidden: !!hidden })) };
    const { data } = await api.put("/sidebar/prefs", payload);
    setPrefs({ layout: data.layout, source: "user" });
  };

  const reset = async () => {
    const { data } = await api.post("/sidebar/prefs/reset");
    setPrefs({ layout: data.layout, source: data.source });
  };

  return { prefs, loading, save, reset };
}

/* ----------------------- Nav Item — normal + edit mode ----------------------- */

function NavItem({ n, activeColor = "#00F0FF" }) {
  return (
    <NavLink to={n.to} end={n.to === "/"} data-testid={n.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${
          isActive ? `bg-white/5 text-white border-l-2 pl-[10px]`
                   : "text-[#8E9196] hover:text-white hover:bg-white/[0.03]"
        }`
      }
      style={({ isActive }) => (isActive ? { borderColor: activeColor } : {})}>
      <n.icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="flex-1">{n.label}</span>
      {n.badge && (
        <span className="font-mono text-[9px] tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded-sm px-1">
          {n.badge}
        </span>
      )}
    </NavLink>
  );
}

function EditableRow({ n, hidden, onToggleHidden, onDragStart, onDragOver, onDrop, isDragging }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => { e.preventDefault(); onDragOver?.(e); }}
      onDrop={onDrop}
      data-testid={`edit-${n.testid}`}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm border border-transparent cursor-grab active:cursor-grabbing transition-all ${
        isDragging ? "opacity-40 border-[#00F0FF]" : "hover:border-white/15 hover:bg-white/[0.03]"
      } ${hidden ? "opacity-50" : ""}`}
    >
      <GripVertical className="w-3.5 h-3.5 text-[#52525B] shrink-0" />
      <n.icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
      <span className="flex-1 truncate">{n.label}</span>
      <button type="button" onClick={onToggleHidden}
        title={hidden ? "Show this tab" : "Hide this tab"}
        data-testid={`toggle-hidden-${n.key}`}
        className="p-1 text-[#8E9196] hover:text-white">
        {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

/* ----------------------- Group renderer ----------------------- */

function Group({ label, items, activeColor, editing, editableList, onDragStart, onDragOver, onDrop, dragKey, onToggleHidden }) {
  if (!editing && items.length === 0) return null;
  return (
    <>
      {label && (
        <div className="pt-4 pb-1 px-3 text-[9px] font-mono uppercase tracking-widest" style={{ color: activeColor === "#B57BFF" ? "#B57BFF" : "#52525B" }}>
          {label}
        </div>
      )}
      {editing
        ? editableList.map((row) => (
            <EditableRow
              key={row.key}
              n={row}
              hidden={row.hidden}
              isDragging={dragKey === row.key}
              onDragStart={(e) => onDragStart(e, row.key)}
              onDragOver={(e) => onDragOver(e, row.key)}
              onDrop={(e) => onDrop(e, row.key)}
              onToggleHidden={() => onToggleHidden(row.key)}
            />
          ))
        : items.map((n) => <NavItem key={n.key} n={n} activeColor={activeColor} />)}
    </>
  );
}

/* ----------------------- Layout ----------------------- */

export default function Layout({ children }) {
  const { user, logout, hasPerm, isSuperAdmin } = useAuth();
  const nav = useNavigate();
  const { prefs, save } = useSidebarPrefs(user);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);   // array of {key, hidden} in current order
  const [dragKey, setDragKey] = useState(null);
  const [saving, setSaving] = useState(false);

  // The read-only composition used when NOT editing.
  const composed = useMemo(
    () => composeSidebar({
      hasPerm,
      isSuperAdmin: !!isSuperAdmin(),
      prefsLayout: prefs.layout,
    }),
    [prefs.layout, hasPerm, isSuperAdmin],
  );

  // Enter edit mode → seed draft from the CURRENT catalog visibility (respecting perms).
  const enterEdit = () => {
    // Only include entries the current user COULD see (perm + platform).
    const visible = NAV_CATALOG.filter((n) => {
      if (n.perm && !hasPerm(n.perm)) return false;
      if (n.group === "platform" && !isSuperAdmin()) return false;
      return true;
    });
    // Start from the persisted order (missing entries appended in catalog order).
    const order = new Map((prefs.layout || []).map((p, i) => [p.key, i]));
    const withOrder = visible.map((n) => ({
      ...n,
      hidden: !!(prefs.layout || []).find((p) => p.key === n.key)?.hidden,
      _ord: order.has(n.key) ? order.get(n.key) : Number.MAX_SAFE_INTEGER,
    }));
    withOrder.sort((a, b) => a._ord - b._ord);
    setDraft(withOrder);
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setDraft(null); setDragKey(null); };

  const saveEdit = async () => {
    if (!draft) return;
    try {
      setSaving(true);
      await save(draft);
      toast.success("Sidebar layout saved");
      setEditing(false);
      setDraft(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onDragStart = (e, key) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData to fire drag events.
    try { e.dataTransfer.setData("text/plain", key); } catch { /* noop */ }
  };
  const onDragOver = (_e, _key) => { /* preventDefault handled by row */ };
  const onDrop = (_e, targetKey) => {
    if (!dragKey || dragKey === targetKey || !draft) { setDragKey(null); return; }
    const from = draft.findIndex((x) => x.key === dragKey);
    const to = draft.findIndex((x) => x.key === targetKey);
    if (from < 0 || to < 0) { setDragKey(null); return; }
    // Only allow reordering WITHIN the same group so main/admin/platform sections stay coherent.
    if (draft[from].group !== draft[to].group) { setDragKey(null); return; }
    const next = [...draft];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDraft(next);
    setDragKey(null);
  };

  const onToggleHidden = (key) => {
    if (!draft) return;
    setDraft(draft.map((x) => x.key === key ? { ...x, hidden: !x.hidden } : x));
  };

  // Split the draft by group for edit-mode rendering.
  const draftGroups = useMemo(() => {
    const g = { main: [], admin: [], platform: [] };
    (draft || []).forEach((row) => { if (g[row.group]) g[row.group].push(row); });
    return g;
  }, [draft]);

  return (
    <div className="h-screen flex bg-[#060606] text-white overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/10 flex flex-col shrink-0 bg-[#0a0a0a] h-screen">
        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
              <div className="relative w-8 h-8 border border-[#00F0FF] rounded-sm flex items-center justify-center">
                <Radar className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.75} />
                <span className="absolute inset-0 blur-md bg-[#00F0FF]/20 -z-10" />
              </div>
              <div className="leading-none">
                <div className="font-display font-black text-lg tracking-tight">FORENSIQ</div>
                <div className="text-[10px] font-mono text-[#8E9196] uppercase tracking-widest">
                  Investigation-first
                </div>
              </div>
            </Link>
            {!editing && (
              <button onClick={enterEdit} data-testid="sidebar-customize-btn"
                title="Reorder and hide sidebar tabs"
                className="p-1.5 rounded-sm text-[#52525B] hover:text-[#00F0FF] hover:bg-white/5">
                <Sliders className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {/* Signature tagline under the logo — mirrored at the bottom of the sidebar for redundancy */}
          <div
            data-testid="brand-signature-top"
            className="mt-3 pt-3 border-t border-white/5 text-[9px] font-mono uppercase tracking-widest text-[#52525B]"
          >
            Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
          </div>
        </div>

        {/* Edit-mode action bar */}
        {editing && (
          <div className="px-3 py-2 border-b border-white/10 bg-[#00F0FF]/[0.05] flex items-center justify-between gap-2"
               data-testid="sidebar-edit-bar">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">
              Customize · drag / hide
            </div>
            <div className="flex gap-1">
              <button onClick={cancelEdit} data-testid="sidebar-edit-cancel"
                title="Discard changes"
                className="p-1 rounded-sm text-[#8E9196] hover:text-white hover:bg-white/5">
                <XCircle className="w-3.5 h-3.5" />
              </button>
              <button onClick={saveEdit} data-testid="sidebar-edit-save" disabled={saving}
                title="Save layout"
                className="p-1 rounded-sm text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black disabled:opacity-40">
                <Save className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto min-h-0">
          <div className="px-1 pb-2">
            <TenantSwitcher />
          </div>

          <Group
            items={composed.main}
            editableList={draftGroups.main}
            editing={editing}
            activeColor="#00F0FF"
            dragKey={dragKey}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onToggleHidden={onToggleHidden}
          />

          {(editing ? draftGroups.admin.length > 0 : composed.admin.length > 0) && (
            <Group
              label="Admin"
              items={composed.admin}
              editableList={draftGroups.admin}
              editing={editing}
              activeColor="#00F0FF"
              dragKey={dragKey}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onToggleHidden={onToggleHidden}
            />
          )}

          {(editing ? draftGroups.platform.length > 0 : composed.platform.length > 0) && (
            <Group
              label="Platform"
              items={composed.platform}
              editableList={draftGroups.platform}
              editing={editing}
              activeColor="#B57BFF"
              dragKey={dragKey}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onToggleHidden={onToggleHidden}
            />
          )}

          {editing && (
            <div className="pt-4 px-3 text-[9px] font-mono uppercase tracking-widest text-[#52525B]">
              Drag rows to reorder within a section · click the eye to hide/show ·
              save from the top bar or open
              <button onClick={() => { cancelEdit(); nav("/settings/sidebar"); }}
                className="ml-1 underline hover:text-[#00F0FF]">full settings</button>
            </div>
          )}
        </nav>

        <div className="border-t border-white/10 px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm truncate" data-testid="current-user-name">{user?.name || "—"}</div>
              <div className="text-[10px] font-mono uppercase text-[#8E9196] tracking-widest">
                {user?.role || "guest"}
              </div>
            </div>
            <button onClick={logout} data-testid="logout-btn"
              className="p-2 rounded-sm text-[#8E9196] hover:text-white hover:bg-white/5 transition-colors"
              title="Sign out">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 text-[9px] font-mono uppercase tracking-widest text-[#52525B] text-center">
            Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-hidden min-w-0">
        {/* `overflow-auto` (not `overflow-y-auto`) so wide content — Live Mail
            config, tables, JSON payloads — can scroll horizontally on narrow
            viewports (curved monitors, Zscaler proxy chrome, split screens)
            instead of being clipped. */}
        <div className="h-full overflow-auto">{children}</div>
      </main>
    </div>
  );
}
